export const BASE_PATH = process.env.PUBLIC_URL;
export const STATIC_PATH = `${BASE_PATH}/static`;
export const PATH_FOR_NETWORK_CHECK = '/network-check'; // this always return 200 from backend

// UUID preprended to XHTML body that indicates XHTML body content is encrypted
// Vaguely named to provide security through obscurity.
export const ENCRYPTION_CONTENT_HEADER = '9d39d85470814754ae30663b8e6aa871';

// Half of decryption key with a misleading name, this has absolutely nothing to do with jQuery.
export const JQUERY = 'xUnBtTMte1ygbnXa';

export const BASE_CFI = '/4/2';
export const HIGHLIGHT_OPACITY = 0.25;

export { DOCUMENT_CSS_TEMPLATE } from './documentCSSTemplate';
export {
  LOCAL_SETTINGS_WITH_DEFAULTS,
  LOCAL_SETTINGS_WITH_DEFAULTS_SPECIFIC_TO_EPUB,
} from './localSettings';
export { APOLLO_CACHE_CONFIG } from './apolloCacheConfig';

// Only user content types that can be placed in the book
export const USER_CONTENT_TYPES_WITH_LOCATIONS = [
  'highlights',
  'notes',
  'bookmarks',
  'copiedTexts',
  'flashcards',
];

// All user content types
export const USER_CONTENT_TYPES =
  USER_CONTENT_TYPES_WITH_LOCATIONS + ['flashcardDecks'];

export const BOOK_DELIMITER_TYPES = [
  'document',
  'marker',
  'topLevelMarker',
  'page',
];

export const EPUB_ANNOTATION_PRESENTATION = {
  HIGHLIGHT_WITH_NOTE: null,
  HIGHLIGHT: 0,
  NOTE: 1,
};

export const DOCUMENT_STATES = {
  NEW: 0,
  HAS_SOURCE: 1,
  HTML_LOADED: 2,
  HTML_ALTERING: 3,
  HTML_ALTERED: 4,
  LOADED: 5,
  FINISHED: 6,
};

export const MOST_RECENT_BOOK_LOCATION_MUTATION_INTERVAL_MILLISECONDS =
  1000 * 5;
export const ACCESS_OFFLINE_CONTENT_DIALOG_TIMEOUT = 1000 * 60 * 5;

export const NETWORK_STATUS_CHECK_INTERVAL_MILLISECONDS = 1000 * 15;

/**
 * Dispatched on `window` once every startup user content query of an offline
 * mobile app session has finished (see `useQueryForUserContent`). The React
 * Native app injects a `window.fetch` wrapper that waits for this event before
 * switching the reader's `window.networkService` to offline. Renaming it breaks
 * the reader-mobile contract described in
 * `RedShelfReader/docs/OFFLINE_OVERVIEW.md`.
 */
export const MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT =
  'redshelf-mobile-startup-queries-complete';

/**
 * Set on `window` while an offline mobile app session boots to tell the app that
 * this reader does dispatch MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT. Without
 * it the app falls back to guessing completion from a quiet period, which older
 * cached readers still need.
 */
export const MOBILE_APP_STARTUP_COMPLETION_SUPPORT_FLAG =
  '__REDSHELF_READER_REPORTS_STARTUP_COMPLETION__';

export const IFRAME_RESIZE_MESSAGE = 'resized for RedShelf';

export const OFFLINE_CHAPTER_STATUS = {
  NOT_DOWNLOADED: 'NOT_DOWNLOADED',
  QUEUE: 'QUEUE',
  DOWNLOADING: 'DOWNLOADING',
  DOWNLOADED: 'DOWNLOADED',
};

export * as ALWAYS_DOWNLOAD_DOCUMENTS from './alwaysDownloadDocuments';
export * as APP_STYLING from './appStyling';
export * as CONTENT_STYLING from './contentStyling';
export * as TEXT_TO_SPEECH from './textToSpeech';
export * as SCREEN_SIZE_CATEGORIES from './screenSizeCategories';

export { default as APA } from './citation-styles/apa.csl';
export { default as CHICAGO } from './citation-styles/chicago-note-bibliography.csl';
export { default as MLA } from './citation-styles/modern-language-association.csl';

export const CACHE_STORAGE_APP_CACHE_NAME = 'app';
export const CACHE_STORAGE_BOOK_CACHE_PREFIX = 'book_';

export const CRASH_DETECTION_INTERVAL = 500;
export const REDSHELF_SOLVE_LINK = 'https://solve.redshelf.com/hc/en-us';

export const MAX_NUMBER_OF_MARKERS_TO_DISPLAY_IN_SLIDER = 200;

export const NAVIGATION_BUTTONS = {
  NEXT: 'Next',
  PREVIOUS: 'Previous',
};

export const PRINTED_FILE_PROXY_BASE_LINK = '/api/printed-file/';
export const PRINTED_FILE_v2_LINK = '/api/printed-file-v2/';

export const INITIAL_V7_COUNTER_VALUE = 3;

export const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

export const DEFAULT_BACK_TO_URL_MOBILEAPP = 'https://redshelf.com/';
export const DEFAULT_BACK_TO_LABEL_MOBILEAPP = 'Back To Shelf';

export const USER_CONTENT_REFETCH = {
  ANNOTATIONS_IN_DOCUMENT: {
    order: 1,
    query: 'annotationsInDocument',
    fieldName: ['epubAnnotationsInDocument'],
  },
  ANNOTATIONS_FROM_SHARES_IN_DOCUMENT: {
    order: 2,
    query: 'annotationsFromSharesInDocument',
    fieldName: ['epubAnnotationsFromSharesInDocument'],
  },
  USER_CONTENT: {
    order: 3,
    query: 'userContent',
    fieldName: [
      'epubBookmarksByEpub',
      'copiedTextsByEpub',
      'flashcardsByEpub',
      'flashcardDecksByEpub',
      'offlineSessionsByEpub',
    ],
  },
  ANNOTATIONS_NOT_IN_DOCUMENT: {
    order: 4,
    query: 'annotationsNotInDocument',
    fieldName: ['epubAnnotationsNotInDocument'],
  },
  ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT: {
    order: 5,
    query: 'annotationsFromSharesNotInDocument',
    fieldName: ['epubAnnotationsFromSharesNotInDocument'],
  },
};

export const ALL_USER_CONTENT_REFETCH = [
  USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_IN_DOCUMENT,
  USER_CONTENT_REFETCH.ANNOTATIONS_IN_DOCUMENT,
  USER_CONTENT_REFETCH.USER_CONTENT,
  USER_CONTENT_REFETCH.ANNOTATIONS_NOT_IN_DOCUMENT,
  USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT,
];

export const OFFLINE_EVENT_TYPES = {
  DOWNLOAD: {
    INDIVIDUAL_CHAPTER: 'individual',
    DOWNLOAD_ALL: 'downloadAll',
  },
  DELETE: {
    INDIVIDUAL_CHAPTER: 'individual',
    CLEAR_ALL: 'clearAll',
  },
};

export const HIGHLIGHT_OVERLAY_RECTANGLE_CLASS = 'rs-inline-display-rectangle';

export const AdditionalMenus = {
  Collaboration: 'Collaboration',
  Keyboard: 'Keyboard Shortcuts',
  Offline: 'Download for Offline',
  Print: 'Print Pages to a PDF',
};

export const additionalMenusArray = [
  AdditionalMenus.Offline,
  AdditionalMenus.Collaboration,
  AdditionalMenus.Keyboard,
  AdditionalMenus.Print,
];

export const annotationColors = {
  yellow: 'Gold',
  green: 'Lime',
  red: 'Tangerine',
  blue: 'Violet',
};

export const CACHE_CONTENT_ACTIONS = {
  UPDATE_APP: 0,
  CLEAR_OUTDATED_BOOK_CONTENT: 1,
  UPDATE_BOOK_CONTENT: 2,
};

export const MAX_NUMBER_OF_CHARACTERS_IN_CARD = 140;
export const WORDS_BEFORE_AND_AFTER_SEARCH_TERM = 5;

export const ANNOTATION_MODALS = {
  flashcard: 'FLASHCARD',
  citation: 'CITATION',
  copy: 'COPY',
};
